import type { HttpClient } from '@shared/http/http-client';
import type {
  IProductionAssignmentRepository,
  ProductionAssignmentListQuery,
} from '@domain/data-contracts/production-assignment-repository.interface';
import type { ProductionAssignment } from '@domain/models/production-assignment.interface';
import type { PagedResult } from '@domain/models/paging.interface';
import {
  mapAssignmentDto,
  mapPaged,
  type ProductionAssignmentDto,
  type PagedDto,
} from './mapping.helper';

const BASE = '/api/production-assignments';

export class ProductionAssignmentRepository implements IProductionAssignmentRepository {
  public constructor(private readonly http: HttpClient) {}

  public async search(
    query: ProductionAssignmentListQuery,
  ): Promise<PagedResult<ProductionAssignment>> {
    const response = await this.http.get<PagedDto<ProductionAssignmentDto>>(BASE, {
      params: pruneParams(query),
    });
    return mapPaged(response.data, mapAssignmentDto);
  }

  public async listByOrderId(orderId: string): Promise<readonly ProductionAssignment[]> {
    const response = await this.http.get<readonly ProductionAssignmentDto[]>(BASE, {
      params: { orderId },
    });
    return response.data.map(mapAssignmentDto);
  }

  public async markStepComplete(assignmentId: string): Promise<ProductionAssignment> {
    const response = await this.http.post<ProductionAssignmentDto>(
      `${BASE}/${encodeURIComponent(assignmentId)}/complete`,
    );
    return mapAssignmentDto(response.data);
  }
}

function pruneParams(
  query: ProductionAssignmentListQuery,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (query.status) { out.status = query.status; }
  if (query.search && query.search.trim().length > 0) { out.search = query.search.trim(); }
  if (typeof query.page === 'number') { out.page = query.page; }
  if (typeof query.pageSize === 'number') { out.pageSize = query.pageSize; }
  return out;
}
